import type {
  ScanEvent,
  ScanProgressEvent,
  KeywordScanProgressEvent,
  ScreenshotProgressEvent,
  ClassificationProgressEvent,
  WebsiteClassifyProgressEvent,
  WebResearchProgressEvent,
  ScanCompleteEvent,
  CellStartedEvent,
  CellCompletedEvent,
} from "@/types/scan-events";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DiscoveredLocation {
  placeId: string;
  name: string;
  lat: number;
  lng: number;
  primaryType: string | null;
  screenshotUrl: string | null;
  hasSportField: boolean | null;
  sportTypes: string[];
  confidence: number | null;
  ownership: string | null;
  processingStage: "discovered" | "screenshotted" | "classified" | "website_classified" | "web_researched";
}

export interface TrackedCell {
  cellId: string;
  centerLat: number;
  centerLng: number;
  radius: number;
  depth: number;
  status: "scanning" | "completed" | "failed";
  startedAt: number;
  completedAt?: number;
}

export interface ScanFeedEvent {
  id: string;
  timestamp: number;
  event: ScanEvent;
}

export interface ScanEventState {
  isConnected: boolean;
  currentPhase: string | null;
  scanStats: ScanProgressEvent | null;
  keywordStats: KeywordScanProgressEvent | null;
  keywordLocationsFound: number;
  screenshotStats: ScreenshotProgressEvent | null;
  classificationStats: ClassificationProgressEvent | null;
  websiteStats: WebsiteClassifyProgressEvent | null;
  webResearchStats: WebResearchProgressEvent | null;
  events: ScanFeedEvent[];
  discoveredLocations: Map<string, DiscoveredLocation>;
  activeCells: Map<string, TrackedCell>;
  isComplete: boolean;
  result: ScanCompleteEvent | null;
  error: string | null;
}

// ── Reducer ───────────────────────────────────────────────────────────────────

type Action =
  | { type: "connected" }
  | { type: "disconnected" }
  | { type: "event"; event: ScanEvent }
  | { type: "reset" };

export const MAX_FEED_EVENTS = 500;
let eventCounter = 0;

export const initialState: ScanEventState = {
  isConnected: false,
  currentPhase: null,
  scanStats: null,
  keywordStats: null,
  keywordLocationsFound: 0,
  screenshotStats: null,
  classificationStats: null,
  websiteStats: null,
  webResearchStats: null,
  events: [],
  discoveredLocations: new Map(),
  activeCells: new Map(),
  isComplete: false,
  result: null,
  error: null,
};

export type ScanAction = Action;

export const SSE_EVENT_TYPES = [
  "phase_change",
  "keyword_scan_progress",
  "keyword_location_found",
  "scan_progress",
  "location_found",
  "cell_started",
  "cell_completed",
  "screenshot_progress",
  "screenshot_complete",
  "classification_progress",
  "classification_complete",
  "website_classify_progress",
  "website_classify_complete",
  "web_research_progress",
  "web_research_complete",
  "scan_complete",
  "scan_error",
] as const;

export function scanReducer(state: ScanEventState, action: Action): ScanEventState {
  switch (action.type) {
    case "connected":
      return { ...state, isConnected: true };

    case "disconnected":
      return { ...state, isConnected: false };

    case "reset":
      eventCounter = 0;
      return { ...initialState, activeCells: new Map(), discoveredLocations: new Map() };

    case "event": {
      const event = action.event;
      const feedEvent: ScanFeedEvent = {
        id: String(++eventCounter),
        timestamp: Date.now(),
        event,
      };

      // Only add certain event types to the visible feed
      const feedEventTypes = new Set([
        "keyword_location_found",
        "location_found",
        "screenshot_complete",
        "classification_complete",
        "website_classify_complete",
        "web_research_complete",
        "scan_complete",
        "scan_error",
      ]);

      const newEvents = feedEventTypes.has(event.type)
        ? [feedEvent, ...state.events].slice(0, MAX_FEED_EVENTS)
        : state.events;

      const newLocations = new Map(state.discoveredLocations);

      switch (event.type) {
        case "phase_change":
          return { ...state, currentPhase: event.phase, events: newEvents };

        case "keyword_scan_progress":
          return { ...state, keywordStats: event, events: newEvents };

        case "keyword_location_found":
          newLocations.set(event.placeId, {
            placeId: event.placeId,
            name: event.name,
            lat: event.lat,
            lng: event.lng,
            primaryType: event.primaryType,
            screenshotUrl: null,
            hasSportField: null,
            sportTypes: [],
            confidence: null,
            ownership: null,
            processingStage: "discovered",
          });
          return {
            ...state,
            discoveredLocations: newLocations,
            keywordLocationsFound: state.keywordLocationsFound + 1,
            events: newEvents,
          };

        case "scan_progress":
          return { ...state, scanStats: event, events: newEvents };

        case "cell_started": {
          const newCells = new Map(state.activeCells);
          newCells.set(event.cellId, {
            cellId: event.cellId,
            centerLat: event.centerLat,
            centerLng: event.centerLng,
            radius: event.radius,
            depth: event.depth,
            status: "scanning",
            startedAt: Date.now(),
          });
          return { ...state, activeCells: newCells, events: newEvents };
        }

        case "cell_completed": {
          // Skip subdivided cells that were never "started" (auto-subdivide path)
          if (event.status === "subdivided" && !state.activeCells.has(event.cellId)) {
            return { ...state, events: newEvents };
          }
          const newCells = new Map(state.activeCells);
          const existing = newCells.get(event.cellId);
          const cellStatus = event.status === "failed" ? "failed" : "completed";
          if (existing) {
            newCells.set(event.cellId, {
              ...existing,
              status: cellStatus,
              completedAt: Date.now(),
            });
          } else {
            // Cell completed without a prior cell_started (e.g. missed event)
            newCells.set(event.cellId, {
              cellId: event.cellId,
              centerLat: event.centerLat,
              centerLng: event.centerLng,
              radius: event.radius,
              depth: event.depth,
              status: cellStatus,
              startedAt: Date.now(),
              completedAt: Date.now(),
            });
          }
          return { ...state, activeCells: newCells, events: newEvents };
        }

        case "location_found":
          newLocations.set(event.placeId, {
            placeId: event.placeId,
            name: event.name,
            lat: event.lat,
            lng: event.lng,
            primaryType: event.primaryType,
            screenshotUrl: null,
            hasSportField: null,
            sportTypes: [],
            confidence: null,
            ownership: null,
            processingStage: "discovered",
          });
          return { ...state, discoveredLocations: newLocations, events: newEvents };

        case "screenshot_progress":
          return { ...state, screenshotStats: event, events: newEvents };

        case "screenshot_complete": {
          const existing = findLocationById(newLocations, event.locationId);
          if (existing) {
            existing.screenshotUrl = event.screenshotUrl;
            existing.processingStage = "screenshotted";
          }
          return { ...state, discoveredLocations: newLocations, events: newEvents };
        }

        case "classification_progress":
          return { ...state, classificationStats: event, events: newEvents };

        case "classification_complete": {
          const existing = findLocationById(newLocations, event.locationId);
          if (existing) {
            existing.hasSportField = event.hasSportField;
            existing.sportTypes = event.sportTypes;
            existing.confidence = event.confidence;
            existing.processingStage = "classified";
          }
          return { ...state, discoveredLocations: newLocations, events: newEvents };
        }

        case "website_classify_progress":
          return { ...state, websiteStats: event, events: newEvents };

        case "website_classify_complete": {
          const existing = findLocationById(newLocations, event.locationId);
          if (existing) {
            existing.ownership = event.ownership;
            existing.processingStage = "website_classified";
          }
          return { ...state, discoveredLocations: newLocations, events: newEvents };
        }

        case "web_research_progress":
          return { ...state, webResearchStats: event, events: newEvents };

        case "web_research_complete": {
          const existing = findLocationById(newLocations, event.locationId);
          if (existing) {
            if (event.ownership) existing.ownership = event.ownership;
            if (event.hasSportField !== null) existing.hasSportField = event.hasSportField;
            if (event.confidence !== null) existing.confidence = event.confidence;
            existing.processingStage = "web_researched";
          }
          return { ...state, discoveredLocations: newLocations, events: newEvents };
        }

        case "scan_complete":
          return {
            ...state,
            isComplete: true,
            result: event,
            events: newEvents,
          };

        case "scan_error":
          return { ...state, error: event.error, events: newEvents };

        default:
          return { ...state, events: newEvents };
      }
    }

    default:
      return state;
  }
}

// Helper: find a DiscoveredLocation by locationId (DB ID, not placeId)
// screenshot_complete and classification_complete use DB location IDs
function findLocationById(
  locations: Map<string, DiscoveredLocation>,
  locationId: string
): DiscoveredLocation | undefined {
  // First try direct placeId lookup
  if (locations.has(locationId)) return locations.get(locationId);
  // Otherwise search by iterating (locationId is the DB ID, not placeId)
  for (const loc of locations.values()) {
    // We don't have the DB ID in DiscoveredLocation, so we can't match perfectly.
    // The pipeline events use DB IDs for screenshot/classification events.
    // For now, return undefined — the location_found event uses placeId.
  }
  return undefined;
}

